import React from "react";
import { Box, Flex, HStack, IconButton, Text } from "@chakra-ui/react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

// 44px touch targets on phones (spec §11), the compact 36px size from md up.
const TOUCH_SIZE = { base: "2.75rem", md: "2.25rem" };

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  pageSize: number;
  /** Total number of items (not pages). */
  total: number;
  onChange: (page: number) => void;
  /** Noun after the count, default "items". */
  itemLabel?: string;
}

/**
 * "1–8 of 42 contracts" + round Previous / Next buttons around a "2 / 6" chip. Page math stays in the page.
 * @example <Pagination page={page} pageSize={8} total={filtered.length} onChange={setPage} itemLabel="tickets" />
 */
export const Pagination: React.FC<PaginationProps> = ({ page, pageSize, total, onChange, itemLabel = "items" }) => {
  const pageCount = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const current = Math.min(Math.max(1, page), pageCount);
  const start = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const end = Math.min(current * pageSize, total);

  return (
    <Flex justify="space-between" align="center" mt={4} gap={3} wrap="wrap">
      <Text textStyle="caption" color="text.muted" aria-live="polite">
        <Box as="span" textStyle="numeral" color="text.primary" fontWeight={700}>
          {start}–{end}
        </Box>{" "}
        of{" "}
        <Box as="span" textStyle="numeral">
          {total}
        </Box>{" "}
        {itemLabel}
      </Text>
      {/* aria-disabled rather than isDisabled: a native `disabled` would blur the button the user just pressed
          and drop keyboard focus to <body>. The button theme's _disabled styles also match [aria-disabled=true]. */}
      <HStack spacing={2}>
        <IconButton
          variant="secondary"
          size="sm"
          isRound
          h={TOUCH_SIZE}
          minW={TOUCH_SIZE}
          aria-label="Previous page"
          icon={<FiChevronLeft />}
          aria-disabled={current <= 1}
          onClick={() => {
            if (!(current <= 1)) onChange(current - 1);
          }}
        />
        <Box
          textStyle="caption"
          bg="sand.100"
          color="text.secondary"
          borderRadius="full"
          px={3}
          h={TOUCH_SIZE}
          display="inline-flex"
          alignItems="center"
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          {current} / {pageCount}
        </Box>
        <IconButton
          variant="secondary"
          size="sm"
          isRound
          h={TOUCH_SIZE}
          minW={TOUCH_SIZE}
          aria-label="Next page"
          icon={<FiChevronRight />}
          aria-disabled={current >= pageCount}
          onClick={() => {
            if (!(current >= pageCount)) onChange(current + 1);
          }}
        />
      </HStack>
    </Flex>
  );
};
